import React, { useState, useEffect, useContext, useRef } from "react"
import * as Yup from "yup"
import { Formik, Form, Field } from "formik"
import { toast } from "react-toastify"
import TextField from "@material-ui/core/TextField"
import CircularProgress from "@material-ui/core/CircularProgress"
import Select from "@material-ui/core/Select"
import InputLabel from "@material-ui/core/InputLabel"
import MenuItem from "@material-ui/core/MenuItem"
import FormControl from "@material-ui/core/FormControl"
import api from "../../services/api"
import toastError from "../../errors/toastError"
import QueueSelect from "../QueueSelect"
import { AuthContext } from "../../context/Auth/AuthContext"
import { Can } from "../Can"
import useWhatsApps from "../../hooks/useWhatsApps"
import DefaultButton from "../DefaultButton"
import { Camera, X as XIcon } from "lucide-react"
import ImageCropper from "../ImageCropper"
import useStyles from "./UserModalStyles"
import Modal from "../Modal"
import PropTypes from "prop-types"
import resolverImagemPerfil from "../../helpers/resolverImagemPerfil"
import { useUsuarioStore } from "../../stores/usuarioStore"
import { useDashboardStore } from "../../stores/dashboardStore"
import UsuarioInativacaoConfirmationModal from "../UsuarioInativacaoConfirmationModal"
import {
  usuarioImpactoInativacaoExtrair,
  usuarioRespostaInativacaoEhConflito,
  usuarioValorInativo,
} from "../../helpers/usuarioInativacao"

const O_USUARIO_INICIAL = {
  name: "",
  nomeAbreviacao: "",
  email: "",
  password: "",
  profile: "user",
  allTicket: "disabled",
  verMensagensMesmoPerfil: true,
  whatsappId: "",
  ativo: true,
  imagemPerfil: null,
}

const UserModal = ({ open, onClose, onSaved, userId, lCadastroEmpresas }) => {
  const classes = useStyles()

  const validacaoSchema = Yup.object().shape({
    name: Yup.string().trim().min(2, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
    nomeAbreviacao: Yup.string().trim().max(3, "Muito longo!"),
    password: userId
      ? Yup.string().min(5, "Muito curto!").max(50, "Muito longo!")
      : Yup.string().min(5, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
    email: Yup.string().trim().email("Email inválido").required("Obrigatório"),
  })

  const { user: loggedInUser } = useContext(AuthContext)

  const [oUsuario, setOUsuario] = useState(O_USUARIO_INICIAL)
  const [mIdsFilasSelecionadas, setMIdsFilasSelecionadas] = useState([])
  const [cWhatsappId, setCWhatsappId] = useState("")
  const { loading: lCarregandoWhatsApps, whatsApps: mWhatsApps } = useWhatsApps()
  const [cPerfilAtual, setCPerfilAtual] = useState("user")
  const lExibirVerMensagensMesmoPerfil = cPerfilAtual !== "admin"

  const oFormikRef = useRef()
  const inputArquivoFotoRef = useRef(null)
  const [cImagemPerfilPreview, setImagemPerfilPreview] = useState(null)
  const [cImagemPerfilOriginal, setImagemPerfilOriginal] = useState(null)
  const [lCropperOpen, setCropperOpen] = useState(false)
  const [oArquivoFoto, setOArquivoFoto] = useState(null)
  const [lFotoRemovida, setLFotoRemovida] = useState(false)
  const [lAtivoOriginal, setLAtivoOriginal] = useState(true)
  const [confirmInativacaoOpen, setConfirmInativacaoOpen] = useState(false)
  const [oDadosPendentesSalvar, setODadosPendentesSalvar] = useState(null)
  const [lInativando, setLInativando] = useState(false)
  const [oImpactoInativacao, setOImpactoInativacao] = useState(null)
  const [lCarregandoImpacto, setLCarregandoImpacto] = useState(false)

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return
      try {
        const { data: oUsuarioRecord } = await api.get(`/users/${userId}`)
        setOUsuario((prevState) => ({ ...prevState, ...oUsuarioRecord }))
        setLAtivoOriginal(oUsuarioRecord.ativo !== false)
        const mIdsFilasRecord = oUsuarioRecord.queues?.map((oFilaRecord) => oFilaRecord.id)
        setMIdsFilasSelecionadas(mIdsFilasRecord)
        setCWhatsappId(oUsuarioRecord.whatsappId ? oUsuarioRecord.whatsappId : "")
        
        const { cUrlImagem } = resolverImagemPerfil(oUsuarioRecord.imagemPerfil)
        setImagemPerfilPreview(cUrlImagem)
      } catch (err) {
        toastError(err)
      }
    }

    fetchUser()
  }, [userId, open])

  useEffect(() => {
    if (oUsuario.profile) {
      setCPerfilAtual(oUsuario.profile)
      if (oUsuario.profile === "admin") {
        setOUsuario((prev) => ({ ...prev, verMensagensMesmoPerfil: true }))
      }
    } else {
      setCPerfilAtual("user")
    }
  }, [oUsuario.profile])

  useEffect(() => {
    if (open && !userId) {
      setOUsuario(O_USUARIO_INICIAL)
      setMIdsFilasSelecionadas([])
      setCWhatsappId("")
      setCPerfilAtual("user")
      setImagemPerfilPreview(null)
      setImagemPerfilOriginal(null)
      setOArquivoFoto(null)
      setLFotoRemovida(false)
      setLAtivoOriginal(true)
      setConfirmInativacaoOpen(false)
      setODadosPendentesSalvar(null)
      setOImpactoInativacao(null)
      setLCarregandoImpacto(false)
    }
  }, [open, userId])

  const handleClose = () => {
    onClose()
    setOUsuario(O_USUARIO_INICIAL)
    setMIdsFilasSelecionadas([])
    setCWhatsappId("")
    setCPerfilAtual("user")
    setImagemPerfilPreview(null)
    setImagemPerfilOriginal(null)
    setOArquivoFoto(null)
    setLFotoRemovida(false)
    setLAtivoOriginal(true)
    setConfirmInativacaoOpen(false)
    setODadosPendentesSalvar(null)
    setOImpactoInativacao(null)
    setLCarregandoImpacto(false)
  }

  const handleClean = () => {
    setOUsuario(O_USUARIO_INICIAL)
    setMIdsFilasSelecionadas([])
    setCWhatsappId("")
    setCPerfilAtual("user")
    setImagemPerfilPreview(null)
    setImagemPerfilOriginal(null)
    setOArquivoFoto(null)
    setLFotoRemovida(false)
  }

  const handleSelecionarFoto = (oEvento) => {
    const oArquivo = oEvento.target.files && oEvento.target.files[0]
    if (!oArquivo) return

    if (!oArquivo.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem válido.")
      return
    }

    const oReader = new FileReader()
    oReader.onload = async () => {
      try {
        setImagemPerfilOriginal(oReader.result)
        setCropperOpen(true)
      } catch (oErro) {
        console.error("Erro ao processar imagem de perfil:", oErro)
        toast.error("Erro ao processar imagem de perfil.")
      }
    }
    oReader.readAsDataURL(oArquivo)
    
    if (oEvento.target) oEvento.target.value = null
  }

  const handleRemoverFoto = () => {
    setImagemPerfilPreview(null)
    setOArquivoFoto(null)
    setLFotoRemovida(true)
  }

  const handleCropConfirm = (oArquivo) => {
    setImagemPerfilPreview(URL.createObjectURL(oArquivo))
    setCropperOpen(false)
    setImagemPerfilOriginal(null)
    setOArquivoFoto(oArquivo)
    setLFotoRemovida(false)
  }

  const salvarImagemPerfil = async (nUsuarioIdSalvo) => {
    if (nUsuarioIdSalvo && (oArquivoFoto || lFotoRemovida)) {
      const oFormData = new FormData()
      if (oArquivoFoto) {
        oFormData.append("imagemPerfil", oArquivoFoto)
      }
      await api.post(`/users/${nUsuarioIdSalvo}/imagemPerfil`, oFormData)
    }
  }

  const finalizarInativacaoComSucesso = () => {
    toast.success("Usuário inativado")
    useUsuarioStore.getState().invalidarTudo()
    useDashboardStore.getState().invalidarTudo()
    onSaved?.()
    handleClose()
  }

  const executarInativacao = async (nUsuarioIdSalvo) => {
    setLInativando(true)
    try {
      await useUsuarioStore.getState().usuarioInativar(nUsuarioIdSalvo, true)
      finalizarInativacaoComSucesso()
    } catch (oErro) {
      toastError(oErro)
    } finally {
      setLInativando(false)
    }
  }

  const iniciarFluxoInativacao = async (nUsuarioIdSalvo, oDadosUsuario) => {
    setODadosPendentesSalvar(oDadosUsuario)
    setConfirmInativacaoOpen(true)
    setLCarregandoImpacto(true)
    setOImpactoInativacao(null)

    try {
      const oImpacto = await useUsuarioStore.getState().usuarioInativacaoImpactoObter(nUsuarioIdSalvo)
      setOImpactoInativacao(usuarioImpactoInativacaoExtrair(oImpacto))
    } catch (oErro) {
      toastError(oErro)
      setConfirmInativacaoOpen(false)
      setODadosPendentesSalvar(null)
      setOImpactoInativacao(null)
    } finally {
      setLCarregandoImpacto(false)
    }
  }

  const handleConfirmarInativacaoModal = async () => {
    if (!userId) return
    setConfirmInativacaoOpen(false)

    try {
      if (oDadosPendentesSalvar) {
        await api.put(`/users/${userId}`, { ...oDadosPendentesSalvar, ativo: true })
        await salvarImagemPerfil(userId)
      }
    } catch (oErro) {
      toastError(oErro)
      return
    }

    await executarInativacao(userId)
  }

  const handleSaveUser = async (values) => {
    const {
      super: lSuperUsuario,
      tokenVersion: nVersaoToken,
      queues: mFilasUsuarias,
      company: oEmpresa,
      id: nId,
      ...oValoresUsuario
    } = values

    if (!values.password) {
      delete oValoresUsuario.password
    }

    if (oValoresUsuario.email) {
      oValoresUsuario.email = oValoresUsuario.email.toLowerCase()
    }

    const oDadosUsuario = {
      ...oValoresUsuario,
      whatsappId: cWhatsappId === "" ? null : cWhatsappId,
      queueIds: mIdsFilasSelecionadas,
      allTicket: cPerfilAtual === "user" ? values.allTicket : "enabled",
      verMensagensMesmoPerfil: cPerfilAtual === "admin" ? true : values.verMensagensMesmoPerfil,
    }

    const lSolicitaInativacao = Boolean(userId && usuarioValorInativo(values.ativo) && lAtivoOriginal)

    if (lSolicitaInativacao) {
      const { ativo: _ativo, ...oDadosSemInativacao } = oDadosUsuario
      await iniciarFluxoInativacao(userId, { ...oDadosSemInativacao, ativo: true })
      return
    }

    try {
      let nUsuarioIdSalvo = userId

      if (userId) {
        await api.put(`/users/${userId}`, oDadosUsuario)
      } else {
        const { data } = await api.post("/users", oDadosUsuario)
        nUsuarioIdSalvo = data.id
      }

      await salvarImagemPerfil(nUsuarioIdSalvo)

      toast.success("Usuário salvo com sucesso.")
      useUsuarioStore.getState().invalidarTudo()
      onSaved?.()
      handleClose()
    } catch (err) {
      if (userId && usuarioRespostaInativacaoEhConflito(err)) {
        const { ativo: _ativo, ...oDadosSemInativacao } = oDadosUsuario
        setOImpactoInativacao(usuarioImpactoInativacaoExtrair(err.response.data))
        setODadosPendentesSalvar({ ...oDadosSemInativacao, ativo: true })
        setConfirmInativacaoOpen(true)
        return
      }
      toastError(err)
    }
  }

  const handleSelectionProfile = (e) => {
    setCPerfilAtual(e.target.value)
    if (e.target.value === "admin") {
      setOUsuario((prev) => ({ ...prev, verMensagensMesmoPerfil: true }))
    }
  }

  const { cUrlImagem: cUrlFotoExibir } = resolverImagemPerfil(cImagemPerfilPreview)

  const footer = (
    <div className={classes.buttonsContainer}>
      <DefaultButton
        stylization="primary"
        onClick={() => oFormikRef.current?.submitForm()}
        disabled={oFormikRef.current?.isSubmitting}
        className={classes.btnFullWidth}
      >
        Gravar
        {oFormikRef.current?.isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
      </DefaultButton>
    </div>
  )

  return (
    <div className={classes.root}>
      <UsuarioInativacaoConfirmationModal
        open={confirmInativacaoOpen}
        onClose={() => {
          setConfirmInativacaoOpen(false)
          setODadosPendentesSalvar(null)
          setOImpactoInativacao(null)
          setLCarregandoImpacto(false)
        }}
        onConfirm={handleConfirmarInativacaoModal}
        lProcessando={lInativando}
        oImpactoInativacao={oImpactoInativacao}
        lCarregandoImpacto={lCarregandoImpacto}
      />
      <Modal
        title={userId ? `Editar usuário` : `Novo usuário`}
        modalOpen={open}
        onClose={onClose}
        cleanFunc={handleClean}
        footer={footer}
      >
        <Formik
          innerRef={oFormikRef}
          initialValues={oUsuario}
          enableReinitialize={true}
          validationSchema={validacaoSchema}
          onSubmit={async (values, actions) => {
            await handleSaveUser(values)
            actions.setSubmitting(false)
          }}
        >
          {({ values, touched, errors }) => (
            <Form>
              <div className={classes.multFieldLine}>
                <Field
                  as={TextField}
                  label="Nome"
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 50 }}
                  className={classes.background + " " + classes.textField}
                />
                <Field
                  as={TextField}
                  label="Abreviação"
                  name="nomeAbreviacao"
                  error={touched.nomeAbreviacao && Boolean(errors.nomeAbreviacao)}
                  helperText={touched.nomeAbreviacao && errors.nomeAbreviacao}
                  variant="outlined"
                  width="25%"
                  inputProps={{ maxLength: 3 }}
                  className={classes.background + " " + classes.textField}
                />
              </div>
              <div className={classes.multFieldLine}></div>
              <div className={classes.multFieldLine}>
                <Field
                  as={TextField}
                  label="Email"
                  name="email"
                  autoComplete="email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  variant="outlined"
                  className={classes.background + " " + classes.textField}
                  fullWidth
                />
              </div>

              <div className={classes.multFieldLine}>
                <Field
                  as={TextField}
                  label="Senha"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 50 }}
                  className={classes.background + " " + classes.textField}
                />
              </div>

              <div className={classes.multFieldLine}>
                <Can
                  key="profile-select"
                  role={loggedInUser.profile}
                  perform="user-modal:editProfile"
                  yes={() => (
                    <FormControl variant="outlined" fullWidth className={classes.background + " " + classes.textField}>
                      <Field name="profile">
                        {({ field, form }) => (
                          <FormControl variant="outlined">
                            <InputLabel id="profile-selection-label">Perfil</InputLabel>
                            <Select
                              {...field}
                              id="profile-selection"
                              labelId="profile-selection-label"
                              value={cPerfilAtual}
                              onChange={(e) => {
                                const { value } = e.target
                                form.setFieldValue(field.name, value)
                                handleSelectionProfile(e)
                              }}
                              label="Perfil"
                              style={{ width: "100%" }}
                              className={classes.background + " " + classes.textField}
                            >
                              <MenuItem value="admin">Administrador</MenuItem>
                              <MenuItem value="supervisor">Supervisor</MenuItem>
                              <MenuItem value="user">Usuário</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                    </FormControl>
                  )}
                />
              </div>

              {lExibirVerMensagensMesmoPerfil && (
                <div className={classes.multFieldLine}>
                  <Can
                    key="view-same-profile-messages"
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => (
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.background + " " + classes.textField}
                      >
                        <InputLabel id="verMensagensMesmoPerfil-label">
                          Ver mensagens de usuários do mesmo perfil
                        </InputLabel>
                        <Field
                          as={Select}
                          name="verMensagensMesmoPerfil"
                          label="Pode ver mensagens de outros atendentes"
                          labelId="verMensagensMesmoPerfil-label"
                        >
                          <MenuItem value={true}>Sim</MenuItem>
                          <MenuItem value={false}>Não</MenuItem>
                        </Field>
                      </FormControl>
                    )}
                  />
                </div>
              )}
              {!lCadastroEmpresas && (
                <>
                  <Can
                    key="queue-select"
                    role={loggedInUser.profile}
                    perform="user-modal:editQueues"
                    yes={() => (
                      <QueueSelect
                        modalOpen={open}
                        selectedQueueIds={mIdsFilasSelecionadas}
                        onChange={(values) => setMIdsFilasSelecionadas(values)}
                        className={classes.background + " " + classes.textField}
                      />
                    )}
                  />
                  <Can
                    key="default-connection"
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => (
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.background + " " + classes.textField}
                      >
                        <InputLabel>Conexão Padrão</InputLabel>
                        <Field
                          as={Select}
                          name="whatsappId"
                          value={cWhatsappId || ""}
                          onChange={(e) => setCWhatsappId(e.target.value || "")}
                          label="Conexão Padrão"
                        >
                          <MenuItem value={""}>&nbsp;</MenuItem>
                          {mWhatsApps.map((whatsapp) => (
                            <MenuItem key={whatsapp.canalId} value={whatsapp.canalId}>
                              {whatsapp.canal.descricao}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    )}
                  />
                </>
              )}
              {cPerfilAtual === "user" && (
                <>
                  <Can
                    key="all-ticket-select"
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() =>
                      !lCarregandoWhatsApps && (
                        <div className={classes.textField}>
                          <FormControl
                            variant="outlined"
                            fullWidth
                            className={classes.background + " " + classes.textField}
                          >
                            <InputLabel id="profile-selection-input-label">Ver conversas Sem Fila</InputLabel>
                            <Field
                              as={Select}
                              name="allTicket"
                              label="Ver conversas Sem Fila"
                              labelId="allTicket-selection-label"
                              id="allTicket-selection"
                              required
                            >
                              <MenuItem value="enabled">Habilitado</MenuItem>
                              <MenuItem value="disabled">Desabilitado</MenuItem>
                            </Field>
                          </FormControl>
                        </div>
                      )
                    }
                  />
                </>
              )}

              <Can
                key="user-status"
                role={loggedInUser.profile}
                perform="user-modal:editProfile"
                yes={() =>
                  !lCarregandoWhatsApps && (
                    <div className={classes.textField}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.background + " " + classes.textField}
                      >
                        <InputLabel id="status-selection-input-label">Status</InputLabel>
                        <Field
                          as={Select}
                          name="ativo"
                          label="Status"
                          labelId="status-selection-label"
                          id="status-selection"
                          required
                        >
                          <MenuItem value={true}>Ativo</MenuItem>
                          <MenuItem value={false}>Inativo</MenuItem>
                        </Field>
                      </FormControl>
                    </div>
                  )
                }
              />
            </Form>
          )}
        </Formik>
        <input
          type="file"
          accept="image/*"
          ref={inputArquivoFotoRef}
          onChange={handleSelecionarFoto}
          style={{ display: "none" }}
          aria-hidden="true"
        />

        <div>
          <div
            style={{
              width: 32,
              height: 20,
              marginBottom: 18,
              fontSize: 15,
              fontWeight: "500",
              lineHeight: "125%",
              letterSpacing: "-0.25%",
              color: "#585757",
              fontFamily: "'Roboto', sans-serif",
            }}
          >
            Foto
          </div>
          <div
            className={classes.photoDropzone}
            role="button"
            tabIndex={0}
            onClick={() => {
              inputArquivoFotoRef.current && inputArquivoFotoRef.current.click()
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (cUrlFotoExibir) {
                  setImagemPerfilOriginal(cUrlFotoExibir)
                  setCropperOpen(true)
                } else {
                  inputArquivoFotoRef.current && inputArquivoFotoRef.current.click()
                }
              }
            }}
          >
            {cUrlFotoExibir ? (
              <>
                <img className={classes.photoCentered} src={cUrlFotoExibir} alt="Foto de perfil" />
                <button
                  type="button"
                  aria-label="Remover foto"
                  title="Remover foto"
                  className={classes.photoRemoveButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoverFoto()
                  }}
                >
                  <XIcon size={14} />
                </button>
              </>
            ) : (
              <>
                <div className={classes.photoIcon} aria-hidden>
                  <Camera size={22} />
                </div>
              </>
            )}
            <div className={classes.photoLabel} aria-hidden="true">
              {cUrlFotoExibir ? "Alterar foto" : "Adicionar foto"}
            </div>
          </div>
        </div>
        <ImageCropper
          lAberto={lCropperOpen}
          aoFechar={() => setCropperOpen(false)}
          cOrigemImagem={cImagemPerfilOriginal}
          aoConfirmar={handleCropConfirm}
        />
      </Modal>
    </div>
  )
}

UserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  userId: PropTypes.number,
  lCadastroEmpresas: PropTypes.bool,
}
UserModal.defaultProps = {
  lCadastroEmpresas: false,
}

export default UserModal
